import type { LedgerLine } from '@/services/financialReports.service';

export type FinanceDrillLine = Pick<
  LedgerLine,
  'voucher_id' | 'voucher_number' | 'reference_number' | 'reference_type' | 'reference_id'
>;

/** Prefer operational reference (BPV / bill / SR); fall back to internal JV number. */
export function getLedgerReferenceLabel(line: Pick<FinanceDrillLine, 'reference_number' | 'voucher_number'>): string {
  const ref = line.reference_number?.trim();
  if (ref) return ref;
  return line.voucher_number?.trim() || '—';
}

export function getVoucherLink(line: FinanceDrillLine): string {
  if (line.reference_type === 'SalesInvoice' && line.reference_id) {
    return `/sales/invoices/${line.reference_id}`;
  }
  if (line.reference_type === 'ReceiptVoucher' && line.reference_id) {
    return `/fin/receipt-vouchers/${line.reference_id}`;
  }
  if (line.reference_type === 'PaymentVoucher' && line.reference_id) {
    return `/fin/payment-vouchers/${line.reference_id}`;
  }
  if (line.reference_type === 'SupplierInvoice' && line.reference_id) {
    return `/scm/procurement/supplier-invoices/${line.reference_id}`;
  }
  if (line.reference_type === 'ServiceReceipt' && line.reference_id) {
    return `/scm/procurement/service-receipts/${line.reference_id}`;
  }
  if (line.reference_type === 'GoodsReceiptNote' && line.reference_id) {
    return `/scm/procurement/grn/${line.reference_id}`;
  }
  if (line.reference_type === 'SupplierAdvanceRefund' && line.reference_id) {
    return `/finance/ap/supplier-advance-refunds/${line.reference_id}`;
  }
  if (line.reference_type === 'StockAdjustment' && line.reference_id) {
    return `/scm/warehouse/stock-adjustment/${line.reference_id}`;
  }
  if (line.reference_type === 'RecognitionContract' && line.reference_id) {
    return `/fin/prepaid/contracts/${line.reference_id}`;
  }
  if (line.reference_type === 'RecognitionScheduleLine' && line.reference_id) {
    return `/fin/prepaid/lines/${line.reference_id}`;
  }
  if (line.reference_type === 'RecognitionPosting' && line.reference_id) {
    return `/fin/prepaid/postings/${line.reference_id}`;
  }
  return `/fin/gl/journals?vid=${line.voucher_id}`;
}

export function formatLedgerBalance(value: number, formatCurrency: (n: number) => string): string {
  if (value >= 0) return formatCurrency(value);
  return `(${formatCurrency(-value)})`;
}

export interface LedgerPageLocationState {
  ledgerType?: 'account' | 'supplier' | 'customer' | 'bank' | 'cash' | 'project';
  accountId?: string;
  fromDate?: string;
  toDate?: string;
}
